import {
  Card,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';
import {
  ScreenError,
  ScreenWrap,
  SearchInput,
  SelectInput,
  SoloLoading,
} from '../../components';
import UserFeedbackRow from './UserFeedbackRow';
import NoteModal from './NoteModal';
import useModifiableParams from '../../hooks/use-modifiable-params';
import { useState } from 'react';
import { Api } from '../../api';

export default function UserFeedbackListScreen() {
  const {
    getParams,
    setModifiedParams,
    tablePaginationProps,
    tableSortLabelProps,
  } = useModifiableParams();

  const params = getParams();

  const [noteModalOpen, setNoteModalOpen] = useState(false);
  const [selectedFeedback, setSelectedFeedback] =
    useState<Api.Feedback.UserV_ListAsAdmin | null>(null);

  const api = Api.Feedback.useReadCollection({
    ...params,
  });

  const openNoteModal = (feedback: Api.Feedback.UserV_ListAsAdmin) => {
    setSelectedFeedback(feedback);
    setNoteModalOpen(true);
  };

  return (
    <ScreenWrap>
      <Stack spacing={3} alignItems="stretch">
        <Stack direction="row" justifyContent="space-between">
          <Typography variant="h4" component="h1">
            User Feedback
          </Typography>
        </Stack>
        <Stack
          direction="row"
          alignItems="end"
          spacing={2}
          sx={{ width: '100%' }}
        >
          <SearchInput
            label="Search"
            value={params.search || ''}
            onChange={(e) =>
              setModifiedParams({ search: e.target.value, page: '1' })
            }
            placeholder="Search by feedback, name or email"
          />
          <SelectInput
            label="Category"
            labelId="category"
            value={params.category || ''}
            onChange={(value) =>
              setModifiedParams({ category: value, page: '1' })
            }
            options={[
              { value: '', label: 'All' },
              { value: 'technical-issue', label: 'Technical Issue' },
              { value: 'feature-idea', label: 'Feature Idea' },
              { value: 'other', label: 'Other' },
            ]}
          />
        </Stack>

        <Stack spacing={2}>
          <Card>
            {api.error ? (
              <ScreenError {...api} />
            ) : api.data?.member ? (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Feedback</TableCell>
                      <TableCell>Category</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>
                        <TableSortLabel {...tableSortLabelProps('submittedAt')}>
                          Submission Date
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>Note</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {api.data.member.map((feedback) => (
                      <UserFeedbackRow
                        key={feedback['@id']}
                        feedback={feedback}
                        onEditNote={openNoteModal}
                        onAddNote={openNoteModal}
                      />
                    ))}
                  </TableBody>
                  <TableFooter
                    sx={{
                      borderTop: '1px solid rgba(224, 224, 224, 1)',
                    }}
                  >
                    <TableRow>
                      <TablePagination
                        {...tablePaginationProps({
                          totalItems: api.data?.totalItems ?? 0,
                          rowsPerPage: 10,
                        })}
                        count={api.data?.totalItems ?? 0}
                        colSpan={6}
                      />
                    </TableRow>
                  </TableFooter>
                </Table>
              </TableContainer>
            ) : (
              <SoloLoading />
            )}
          </Card>
        </Stack>
      </Stack>

      {noteModalOpen && selectedFeedback && (
        <NoteModal
          open={noteModalOpen}
          onClose={() => setNoteModalOpen(false)}
          feedback={selectedFeedback}
        />
      )}
    </ScreenWrap>
  );
}
